import { getCurrentCryptoPrice, getCurrentCryptoPrices } from './coingecko';
import { Sentry } from './sentry';
import type { TokenSummary } from './tokenAggregation';

/**
 * Calcule les champs dérivés (valeur totale, P&L) pour un prix donné. Exporté pour permettre à
 * l'UI de "pré-rendre" un token avec le dernier prix connu (cache localStorage) avant même que
 * `updateTokenPrices` n'ait résolu son appel réseau.
 */
export const priceToken = (token: TokenSummary, currentPrice: number): TokenSummary => {
  if (token.crypto.id.startsWith('custom-')) {
    return {
      ...token,
      currentPrice: token.averagePurchasePrice, // Utiliser le prix d'achat moyen
      currentTotalValue: token.totalAmount * token.averagePurchasePrice,
      profitLoss: 0,
      profitLossPercentage: 0,
    };
  }

  const currentTotalValue = token.totalAmount * currentPrice;
  const profitLoss = currentTotalValue - token.totalValueAtPurchase;
  const profitLossPercentage = (profitLoss / token.totalValueAtPurchase) * 100;

  return {
    ...token,
    currentPrice,
    currentTotalValue,
    profitLoss,
    profitLossPercentage,
  };
};

const unavailablePriceToken = (token: TokenSummary): TokenSummary => ({
  // API unavailable — leave currentPrice at 0 so the UI shows the manual input
  ...token,
  currentPrice: 0,
  currentTotalValue: 0,
  profitLoss: 0,
  profitLossPercentage: 0,
});

/**
 * Récupère le prix courant de chaque token et retourne le tableau mis à jour. Si `onTokenPriced`
 * est fourni, il est appelé dès qu'un token individuel est résolu pour permettre un rendu
 * incrémental côté UI.
 *
 * Les tokens non-custom sont récupérés en un seul appel groupé (`getCurrentCryptoPrices`,
 * CoinGecko `/coins/markets`) plutôt qu'un appel par token, ce qui élimine l'essentiel de la
 * latence du premier chargement. Un coin absent de la réponse groupée retombe sur
 * `getCurrentCryptoPrice` (fallback LiveCoinWatch inclus) pour ce token individuellement.
 */
export const updateTokenPrices = async (
  tokenSummaries: TokenSummary[],
  onTokenPriced?: (token: TokenSummary) => void
): Promise<TokenSummary[]> => {
  const priceableIds = tokenSummaries
    .filter((token) => !token.crypto.id.startsWith('custom-'))
    .map((token) => token.crypto.id);

  const bulkPrices = priceableIds.length > 0 ? await getCurrentCryptoPrices(priceableIds) : {};

  const updatedTokens = await Promise.all(
    tokenSummaries.map(async (token) => {
      try {
        // Ne pas récupérer le prix pour les tokens custom
        if (token.crypto.id.startsWith('custom-')) {
          const priced = priceToken(token, token.averagePurchasePrice);
          onTokenPriced?.(priced);
          return priced;
        }

        let currentPrice = bulkPrices[token.crypto.id] ?? null;
        if (currentPrice === null) {
          currentPrice = await getCurrentCryptoPrice(token.crypto.id);
        }

        const priced = currentPrice !== null ? priceToken(token, currentPrice) : unavailablePriceToken(token);
        onTokenPriced?.(priced);
        return priced;
      } catch (error) {
        console.error(`Erreur lors de la récupération du prix pour ${token.crypto.name}:`, error);
        Sentry.captureException(error, {
          tags: { operation: 'updateTokenPrices' },
          extra: { tokenId: token.crypto.id, tokenName: token.crypto.name },
        });
        const priced = unavailablePriceToken(token);
        onTokenPriced?.(priced);
        return priced;
      }
    })
  );

  return updatedTokens;
};
